import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  Clipboard,
  Copy,
  FileCheck2,
  FileText,
  LockKeyhole,
  PhoneCall,
  Printer,
  RotateCcw,
  Route,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Target,
  TimerOff,
  Wrench,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DemoAgenticAssistant } from '@/components/surgeon/v2/demo-agentic/DemoAgenticAssistant';
import { cn } from '@/lib/utils';
import { buildP2PDemoAgentExperience } from '@/lib/demo/demoAgenticExperience';
import {
  buildP2PDemoCheatsheet,
  type P2PDemoEvidenceFinding,
  type P2PDemoOutcome,
  type P2PDemoSession,
  type P2PDemoView,
} from '@/lib/p2pAppeals';

interface P2PGenieWorkspaceProps {
  session: P2PDemoSession;
  view: P2PDemoView;
  onBack: () => void;
  onApplyCodingCorrection: () => void;
  onOutcome: (outcome: P2PDemoOutcome) => void;
  onRouteToOps: () => void;
  onReturnToOr: () => void;
  onReset: () => void;
}

const OUTCOME_STAGES = new Set([
  'appeal_ready',
  'documentation_review_required',
  'routed',
]);

const PHASE_ICONS = {
  credentials: BadgeCheck,
  rebuttal: ShieldAlert,
  evidence: FileCheck2,
  demand: Target,
} as const;

function EvidenceStatus({ finding }: { finding: P2PDemoEvidenceFinding }) {
  const config = {
    met: {
      label: finding.resultLabel || 'Threshold met',
      icon: CheckCircle2,
      className: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    },
    exception: {
      label: finding.resultLabel || 'Exception applies',
      icon: ShieldCheck,
      className: 'border-sky-300 bg-sky-50 text-sky-800',
    },
    warning: {
      label: finding.resultLabel || 'Threshold unmet',
      icon: AlertTriangle,
      className: 'border-amber-300 bg-amber-50 text-amber-800',
    },
    blocked: {
      label: finding.resultLabel || 'Action required',
      icon: XCircle,
      className: 'border-red-300 bg-red-50 text-red-800',
    },
  }[finding.status];
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn('gap-1.5 whitespace-normal text-left', config.className)}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {config.label}
    </Badge>
  );
}

function printLetter(title: string, body: string) {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer');
  if (!printWindow) {
    toast.error('Print window was blocked');
    return;
  }

  const heading = printWindow.document.createElement('h1');
  heading.textContent = title;
  const content = printWindow.document.createElement('pre');
  content.textContent = body;
  const style = printWindow.document.createElement('style');
  style.textContent = 'body{font-family:Georgia,serif;margin:48px;color:#111827}h1{font-family:Arial,sans-serif;font-size:18px;margin-bottom:28px}pre{white-space:pre-wrap;font:14px/1.65 Georgia,serif}';
  printWindow.document.head.appendChild(style);
  printWindow.document.body.append(heading, content);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export function P2PGenieWorkspace({
  session,
  view,
  onBack,
  onApplyCodingCorrection,
  onOutcome,
  onRouteToOps,
  onReturnToOr,
  onReset,
}: P2PGenieWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<'call' | 'appeal'>(
    OUTCOME_STAGES.has(session.stage) ? 'appeal' : 'call',
  );
  const { scenario } = view;
  const demoAgentExperience = useMemo(
    () => buildP2PDemoAgentExperience(session, view),
    [session, view],
  );
  const outcomeAvailable = !view.blocked;
  const opsAvailable = OUTCOME_STAGES.has(session.stage);
  const strategyConfig = {
    direct_match: {
      icon: CheckCircle2,
      className: 'border-emerald-300 bg-emerald-50 text-emerald-950',
      iconClassName: 'text-emerald-700',
    },
    policy_exception: {
      icon: ShieldCheck,
      className: 'border-sky-300 bg-sky-50 text-sky-950',
      iconClassName: 'text-sky-700',
    },
    correctable_defect: {
      icon: Wrench,
      className: 'border-amber-300 bg-amber-50 text-amber-950',
      iconClassName: 'text-amber-700',
    },
  }[scenario.strategy.type];
  const StrategyIcon = strategyConfig.icon;

  useEffect(() => {
    if (OUTCOME_STAGES.has(session.stage)) setActiveTab('appeal');
  }, [session.stage]);

  if (session.stage === 'overturned') {
    return (
      <section className="overflow-hidden rounded-lg border border-emerald-300 bg-card">
        <div className="border-b border-emerald-200 bg-emerald-50 px-5 py-4">
          <Badge variant="outline" className="border-emerald-300 bg-white text-emerald-800">
            Simulated P2P outcome
          </Badge>
        </div>
        <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-14 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700">
            <Check className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-2xl font-semibold text-foreground">Authorization overturned</h2>
          <p className="mt-3 max-w-xl text-sm leading-6 text-muted-foreground">
            {scenario.payerName} accepted the submitted evidence for {scenario.patientName}. The case can return to the existing OR reconciliation path.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button onClick={onReturnToOr} className="gap-2">
              Return Case to OR Queue
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button onClick={onReset} variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset Scenario
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const copyCheatsheet = async () => {
    try {
      await navigator.clipboard.writeText(buildP2PDemoCheatsheet(view));
      toast.success('Tactical cheatsheet copied');
    } catch {
      toast.error('Unable to copy the tactical cheatsheet');
    }
  };

  const copyAppeal = async () => {
    if (!view.appeal) return;
    try {
      await navigator.clipboard.writeText(view.appeal.body);
      toast.success('Appeal copied to clipboard');
    } catch {
      toast.error('Unable to copy the appeal');
    }
  };

  return (
    <section className="space-y-5">
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-4 border-b border-border p-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="-ml-3 mb-3 gap-2 text-muted-foreground">
              <ArrowLeft className="h-4 w-4" />
              Command Center
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary">
                <Shield className="h-3.5 w-3.5" />
                P2PGenie Tactician
              </Badge>
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                Simulated payer denial
              </Badge>
            </div>
            <h2 className="mt-3 text-xl font-semibold text-foreground sm:text-2xl">
              {scenario.patientName} · {scenario.procedureName}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {scenario.payerName} / {scenario.umVendor} · {scenario.cptCodes.join(', ')}
            </p>
          </div>

          <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:w-[390px]">
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <p className="text-xs font-medium uppercase text-muted-foreground">Denial received</p>
              <p className="mt-2 text-sm font-semibold text-foreground">{view.denialDateLabel}</p>
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs font-medium uppercase text-amber-800">P2P deadline</p>
              <p className="mt-2 text-sm font-semibold text-amber-950">{view.p2pDeadlineLabel}</p>
            </div>
          </div>
        </div>

        <div className="border-b border-border p-4 sm:px-5">
          <DemoAgenticAssistant experience={demoAgentExperience} />
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'call' | 'appeal')}>
          <div className="border-b border-border px-5 py-3">
            <TabsList className="grid h-10 w-full max-w-md grid-cols-2">
              <TabsTrigger value="call">P2P Tactical Protocol</TabsTrigger>
              <TabsTrigger value="appeal" disabled={!opsAvailable}>LINK Ops Handoff</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="call" className="m-0">
            <div className="grid xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0">
                <section className="p-5 sm:p-6" aria-labelledby="tactical-cheatsheet-heading">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Shield className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-primary">Live call intelligence</p>
                        <h3 id="tactical-cheatsheet-heading" className="mt-1 text-lg font-semibold text-foreground sm:text-xl">
                          P2P Tactical Cheatsheet & Escalation Protocol
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          Compiled from the submitted authorization snapshot and the payer response.
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={copyCheatsheet} className="shrink-0 gap-2 self-start">
                      <Copy className="h-4 w-4" />
                      Copy Call Sheet
                    </Button>
                  </div>

                  <dl className="mt-6 divide-y divide-border border-y border-border">
                    <div className="grid gap-1 py-3 sm:grid-cols-[190px_minmax(0,1fr)]">
                      <dt className="text-xs font-medium uppercase text-muted-foreground">Payer / UM Vendor</dt>
                      <dd className="text-sm font-semibold text-foreground">{scenario.payerName} / {scenario.umVendor}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[190px_minmax(0,1fr)]">
                      <dt className="text-xs font-medium uppercase text-muted-foreground">Denied Procedure</dt>
                      <dd className="text-sm leading-6 text-foreground">{scenario.cptCodes.join(', ')} · {scenario.procedureName}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[190px_minmax(0,1fr)]">
                      <dt className="text-xs font-medium uppercase text-muted-foreground">Reported Denial Rationale</dt>
                      <dd className="text-sm leading-6 text-foreground">
                        <span className="font-semibold">{scenario.denialCodes.join(' / ')}</span> - {scenario.denialRationale}
                      </dd>
                    </div>
                  </dl>
                </section>

                <div className="border-t border-border">
                  {scenario.callPhases.map((phase) => {
                    const PhaseIcon = PHASE_ICONS[phase.id];
                    return (
                      <section key={phase.id} className="grid gap-4 border-b border-border p-5 sm:grid-cols-[50px_minmax(0,1fr)] sm:p-6">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-primary/20 bg-primary/5 font-mono text-sm font-semibold text-primary">
                          {phase.number}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-start gap-2">
                            <PhaseIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                            <div>
                              <p className="text-xs font-semibold uppercase text-primary">Phase {Number(phase.number)}</p>
                              <h4 className="mt-1 text-base font-semibold text-foreground">{phase.title}</h4>
                            </div>
                          </div>

                          {phase.id === 'evidence' ? (
                            <div className="mt-4 divide-y divide-border border-y border-border">
                              {scenario.evidencePayload.map((item) => (
                                <div key={item.id} className="grid gap-1 py-3 md:grid-cols-[170px_minmax(0,1fr)]">
                                  <p className="text-sm font-semibold text-foreground">{item.label}</p>
                                  <p className="text-sm leading-6 text-muted-foreground">{item.summary}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 border-l-2 border-primary/35 bg-muted/25 px-4 py-3">
                              <p className="text-xs font-semibold uppercase text-muted-foreground">{phase.scriptLabel}</p>
                              <blockquote className="mt-2 text-sm font-medium leading-7 text-foreground">
                                “{phase.script}”
                              </blockquote>
                            </div>
                          )}

                          {phase.id === 'credentials' && (
                            <div className="mt-3 border-l-2 border-amber-400 bg-amber-50 px-4 py-3">
                              <p className="text-xs font-semibold uppercase text-amber-800">Vendor Alert</p>
                              <p className="mt-2 text-sm leading-6 text-amber-950">{scenario.vendorAlert}</p>
                            </div>
                          )}
                        </div>
                      </section>
                    );
                  })}

                  {scenario.appeal && (
                    <section className="grid gap-4 border-b border-border bg-slate-50/70 p-5 sm:grid-cols-[50px_minmax(0,1fr)] sm:p-6">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-primary/20 bg-white font-mono text-sm font-semibold text-primary">
                        05
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex items-start gap-2">
                            <Route className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                            <div>
                              <p className="text-xs font-semibold uppercase text-primary">Phase 5</p>
                              <h4 className="mt-1 text-base font-semibold text-foreground">Automated Escalation & Formal Appeal Generation</h4>
                            </div>
                          </div>
                          <Badge variant="outline" className="w-fit border-primary/25 bg-white text-primary">
                            {view.appeal ? 'Draft compiled' : 'Armed before call'}
                          </Badge>
                        </div>

                        <p className="mt-4 text-sm leading-6 text-muted-foreground">{scenario.appeal.trigger}</p>
                        <dl className="mt-4 divide-y divide-border border-y border-border">
                          <div className="grid gap-1 py-3 md:grid-cols-[180px_minmax(0,1fr)]">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">Escalation Pathway</dt>
                            <dd className="text-sm font-semibold text-foreground">{scenario.appeal.pathwayLabel}</dd>
                          </div>
                          <div className="grid gap-1 py-3 md:grid-cols-[180px_minmax(0,1fr)]">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">Submission Deadline</dt>
                            <dd className="text-sm leading-6 text-foreground">
                              {scenario.appeal.deadlineRule}
                              {view.escalationDeadlineLabel && <span className="ml-1 font-semibold">Current case: {view.escalationDeadlineLabel}.</span>}
                            </dd>
                          </div>
                          <div className="grid gap-1 py-3 md:grid-cols-[180px_minmax(0,1fr)]">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">Requirement</dt>
                            <dd className="text-sm leading-6 text-foreground">{scenario.appeal.requirement}</dd>
                          </div>
                        </dl>
                      </div>
                    </section>
                  )}
                </div>

                <section className="p-5 sm:p-6" aria-labelledby="evidence-match-heading">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase text-primary">Audit-ready trace</p>
                      <h3 id="evidence-match-heading" className="mt-1 text-base font-semibold text-foreground">LINK Evidence Match</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Submitted packet snapshot only</p>
                  </div>

                  <div className="mt-4 divide-y divide-border border-y border-border">
                    {view.evidence.map((finding) => (
                      <div key={finding.id} className="grid gap-3 py-4 lg:grid-cols-[minmax(150px,0.75fr)_minmax(0,1.5fr)_minmax(180px,1fr)_auto]">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{finding.label}</p>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">{finding.source}</p>
                        </div>
                        <p className="text-sm leading-6 text-foreground">{finding.clinicalFact}</p>
                        <p className="text-xs leading-5 text-muted-foreground">{finding.policyThreshold}</p>
                        <div className="lg:justify-self-end">
                          <EvidenceStatus finding={finding} />
                        </div>
                        {finding.remediation && (
                          <p className="border-l-2 border-border pl-3 text-xs leading-5 text-muted-foreground lg:col-span-4">
                            <strong className="font-medium text-foreground">Tactical instruction:</strong> {finding.remediation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <aside className="border-t border-border bg-muted/15 p-5 xl:border-l xl:border-t-0">
                <div className="space-y-5 xl:sticky xl:top-4">
                  <section className={cn('rounded-md border p-4', strategyConfig.className)}>
                    <div className="flex items-start gap-3">
                      <StrategyIcon className={cn('mt-0.5 h-5 w-5 shrink-0', strategyConfig.iconClassName)} />
                      <div>
                        <p className="text-sm font-semibold">{scenario.strategy.label}</p>
                        <p className="mt-2 text-xs leading-5 opacity-80">{scenario.strategy.summary}</p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-amber-700" />
                      <p className="text-sm font-semibold text-foreground">P2P call window</p>
                    </div>
                    <p className="mt-2 text-base font-semibold text-foreground">{view.p2pDeadlineLabel}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{scenario.p2pWindowBusinessDays} business days from denial</p>
                  </section>

                  <Separator />

                  <section>
                    <p className="text-xs font-medium uppercase text-muted-foreground">Policy Match</p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-foreground">{scenario.policyName}</p>
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">Fixed synthetic ruleset for VC demonstration. Validate against the controlling plan document before production use.</p>
                  </section>

                  <section>
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold text-foreground">Reviewer credential check</p>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                      Request an exact match for {scenario.reviewerSpecialty} and record authority to reverse the denial.
                    </p>
                  </section>

                  {scenario.id === 'thomas-palmer' && !session.codingCorrectionApplied && (
                    <section className="rounded-md border border-red-300 bg-red-50 p-3">
                      <p className="text-sm font-semibold text-red-950">Laterality correction required</p>
                      <p className="mt-2 text-xs leading-5 text-red-900/80">
                        Align modifier LT with the submitted left-shoulder evidence before recording an outcome.
                      </p>
                      <Button onClick={onApplyCodingCorrection} size="sm" className="mt-3 w-full gap-2">
                        <Wrench className="h-4 w-4" />
                        Apply LT Correction
                      </Button>
                    </section>
                  )}

                  {view.blocked && scenario.id !== 'thomas-palmer' && (
                    <section className="rounded-md border border-red-300 bg-red-50 p-3">
                      <p className="text-sm font-semibold text-red-950">Evidence action required</p>
                      <p className="mt-2 text-xs leading-5 text-red-900/80">{scenario.documentationReviewReason}</p>
                      <Button onClick={() => onOutcome('failed')} variant="outline" size="sm" className="mt-3 w-full border-red-300 text-red-800">
                        Route for Evidence Review
                      </Button>
                    </section>
                  )}

                  {outcomeAvailable && (
                    <section>
                      <p className="text-xs font-medium uppercase text-muted-foreground">Record simulated outcome</p>
                      <div className="mt-3 grid gap-2">
                        <Button onClick={() => onOutcome('overturned')} className="justify-start gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
                          <CheckCircle2 className="h-4 w-4" />
                          Authorization Overturned
                        </Button>
                        <Button
                          onClick={() => onOutcome('failed')}
                          variant="outline"
                          className="justify-start gap-2 border-red-300 text-red-800 hover:bg-red-50 hover:text-red-900"
                        >
                          <XCircle className="h-4 w-4" />
                          P2P Denied · Draft Appeal
                        </Button>
                        <Button onClick={() => onOutcome('timed_out')} variant="outline" className="justify-start gap-2">
                          <TimerOff className="h-4 w-4" />
                          P2P Timed Out
                        </Button>
                      </div>
                    </section>
                  )}

                  <Separator />

                  <section>
                    <div className="flex items-center gap-2">
                      <LockKeyhole className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold text-foreground">Evidence boundary</p>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                      Every clinical fact is compiled from the frozen submitted packet and payer response. Unsent builder state is excluded.
                    </p>
                  </section>
                </div>
              </aside>
            </div>
          </TabsContent>

          <TabsContent value="appeal" className="m-0">
            {session.stage === 'documentation_review_required' ? (
              <div className="grid xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="p-6 sm:p-8">
                  <div className="max-w-2xl">
                    <Badge variant="outline" className="border-red-300 bg-red-50 text-red-800">Appeal compilation held</Badge>
                    <h3 className="mt-4 text-xl font-semibold text-foreground">Documentation review required</h3>
                    <p className="mt-3 text-sm leading-7 text-muted-foreground">{scenario.documentationReviewReason || 'A blocked criterion must be resolved before appeal compilation.'}</p>
                    <div className="mt-6 border-l-2 border-red-300 bg-red-50/60 p-4">
                      <p className="text-sm font-semibold text-foreground">Why LINK stopped</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        The generated appeal cannot state that a policy threshold was met when the submitted packet does not support that assertion.
                      </p>
                    </div>
                  </div>
                </div>
                <aside className="border-t border-border bg-muted/15 p-5 xl:border-l xl:border-t-0">
                  <Button onClick={onReset} variant="outline" className="w-full gap-2">
                    <RotateCcw className="h-4 w-4" />
                    Reset Scenario
                  </Button>
                </aside>
              </div>
            ) : view.appeal ? (
              <div className="grid xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="min-w-0 p-5 sm:p-6">
                  <div className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            session.outcome === 'failed'
                              ? 'border-red-300 bg-red-50 text-red-800'
                              : 'border-amber-300 bg-amber-50 text-amber-800',
                          )}
                        >
                          {session.outcome === 'failed' ? 'P2P denied' : 'P2P timed out'}
                        </Badge>
                        <Badge className="bg-primary text-primary-foreground hover:bg-primary">Appeal auto-drafted</Badge>
                      </div>
                      <h3 className="mt-3 text-xl font-semibold text-foreground">Generated Appeal Draft</h3>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{view.appeal.subject}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="icon" onClick={copyAppeal} aria-label="Copy appeal" title="Copy appeal">
                        <Clipboard className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => printLetter(view.appeal!.title, view.appeal!.body)}
                        aria-label="Print appeal"
                        title="Print appeal"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div
                    role="status"
                    aria-live="polite"
                    className="mt-5 flex gap-3 rounded-md border border-primary/25 bg-primary/5 p-4"
                  >
                    <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Appeal drafted automatically</p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        LINK compiled this payer-specific draft from the frozen submitted authorization, denial response, and source-linked evidence. Surgeon review and approval remain required before submission.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 border-b border-border py-5 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium uppercase text-muted-foreground">Escalation pathway</p>
                      <p className="mt-2 text-sm font-semibold text-foreground">{scenario.appeal?.pathwayLabel}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-muted-foreground">Decision request</p>
                      <p className="mt-2 text-sm font-semibold text-foreground">{view.appeal.decisionWindow}</p>
                    </div>
                  </div>

                  <article className="mt-6 whitespace-pre-wrap rounded-md border border-border bg-white p-5 font-serif text-sm leading-7 text-slate-900 sm:p-7">
                    {view.appeal.body}
                  </article>
                </div>

                <aside className="space-y-5 border-t border-border bg-muted/15 p-5 xl:border-l xl:border-t-0">
                  <section>
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold text-foreground">Filing deadline</p>
                    </div>
                    <p className="mt-2 text-lg font-semibold text-foreground">
                      {format(parseISO(view.appeal.submissionDeadline), 'MMM d, yyyy')}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{scenario.appeal?.deadlineRule}</p>
                  </section>

                  <Separator />

                  <section>
                    <p className="text-xs font-medium uppercase text-muted-foreground">Evidence checklist</p>
                    <div className="mt-3 space-y-3">
                      {view.evidence.map((finding) => (
                        <div key={finding.id} className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
                          {finding.status === 'warning' ? (
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
                          ) : finding.status === 'exception' ? (
                            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" />
                          ) : (
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
                          )}
                          <span>{finding.label}: {finding.resultLabel || (finding.status === 'met' ? 'threshold met' : finding.status)}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <Separator />

                  {session.stage === 'routed' ? (
                    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
                      <div className="flex items-center gap-2 text-emerald-800">
                        <ShieldCheck className="h-4 w-4" />
                        <p className="text-sm font-semibold">Routed to LINK Ops</p>
                      </div>
                      <p className="mt-2 text-xs leading-5 text-emerald-900/75">
                        Simulated handoff complete. No external system was contacted.
                      </p>
                    </div>
                  ) : (
                    <Button onClick={onRouteToOps} className="w-full gap-2">
                      <Route className="h-4 w-4" />
                      Route to LINK Ops
                    </Button>
                  )}

                  <Button onClick={onReset} variant="ghost" className="w-full gap-2 text-muted-foreground">
                    <RotateCcw className="h-4 w-4" />
                    Reset Scenario
                  </Button>
                </aside>
              </div>
            ) : (
              <div className="p-8 text-sm text-muted-foreground">Appeal output is unavailable for this scenario.</div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <p className="text-xs text-muted-foreground">
            {session.auditEvents.length} synthetic workflow events preserved in this browser session.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">No payer, EHR, or billing system was contacted.</p>
      </div>
    </section>
  );
}
